import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { catchError, map, Observable, of } from 'rxjs';

import { APOLLO_CLIENT_NAMES } from '@customer-portal/shared/constants';
import {
  apolloThrowIfNotSuccess,
  getQueryRootFieldName,
} from '@customer-portal/shared/helpers/custom-operators';
import { BaseApolloResponse } from '@customer-portal/shared/models/response/base-apollo.response';

import { ProfileDto } from '../../../dtos';
import {
  PROFILE_QUERY,
  PROFILE_SETTINGS_MUTATION,
  UPDATE_LOGIN_TIME_MUTATION,
} from '../../../graphql';
import { UpdateLoginTimeResponse } from '../../../models/update-login-time-response';

@Injectable({ providedIn: 'root' })
export class ProfileService {
  private clientName = APOLLO_CLIENT_NAMES.contact;

  constructor(private readonly apollo: Apollo) {}

  getProfileData(): Observable<ProfileDto | null> {
    const fieldName = getQueryRootFieldName(PROFILE_QUERY) || 'userProfile';

    return this.apollo
      .use(this.clientName)
      .query({
        query: PROFILE_QUERY,
        variables: {},
        fetchPolicy: 'no-cache',
      })
      .pipe(
        apolloThrowIfNotSuccess(fieldName),
        map((result: any) => result?.data?.userProfile),
        catchError((_) => of(null)),
      );
  }

  updateProfileSettingsData(jobTitle: string): Observable<any> {
    const fieldName =
      getQueryRootFieldName(PROFILE_SETTINGS_MUTATION) || 'updateProfile';

    return this.apollo
      .use(this.clientName)
      .mutate({
        mutation: PROFILE_SETTINGS_MUTATION,
        variables: {
          jobTitle,
        },
      })
      .pipe(
        apolloThrowIfNotSuccess(fieldName),
        map((result: any) => result?.data?.updateProfile),
      );
  }

  updateLoginTime(loginTime: string): Observable<UpdateLoginTimeResponse> {
    return this.apollo
      .use(this.clientName)
      .mutate<{ updateLoginTime: BaseApolloResponse<number> }>({
        mutation: UPDATE_LOGIN_TIME_MUTATION,
        variables: {
          loginTime,
        },
        context: {
          headers: new HttpHeaders().set('SKIP_LOADING', 'true'),
        },
      })
      .pipe(
        map((result) => {
          if (!result || !result.data) {
            return this.getDefaultUpdateLoginTimeError();
          }

          return result.data as UpdateLoginTimeResponse;
        }),
        catchError(() => of(this.getDefaultUpdateLoginTimeError())),
      );
  }

  private getDefaultUpdateLoginTimeError(
    message = 'Update Login Time Error',
    errorCode = 'Update Login Time Error',
  ): UpdateLoginTimeResponse {
    return {
      updateLoginTime: {
        isSuccess: false,
        data: 0,
        errorCode,
        message,
      },
    };
  }
}
